/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft,
  Article,
  Plus,
  PencilSimple,
  Trash,
  FloppyDisk,
  Eye,
  EyeSlash,
  Globe,
} from "@phosphor-icons/react";

// SLATE theme — keeps each Super Admin tool visually distinct.
const TH = {
  bg:        "#0F1623",
  surface:   "#172234",
  surfaceHi: "#1F2D45",
  border:    "#2B3A56",
  borderHi:  "#3A4E72",
  accent:    "#F5C16C",      // amber-warm
  accentDim: "#8C6638",
  text:      "#EAF0FA",
  textMute:  "#90A1BD",
  textDim:   "#566A8A",
  green:     "#4ADE9C",
  red:       "#FB7185",
};

const EMPTY_FORM = {
  post_id: null,
  slug: "",
  title: "",
  excerpt: "",
  body_markdown: "",
  featured_image_url: "",
  author_name: "AstroKuberChat Team",
  seo_title: "",
  seo_description: "",
  published: true,
};

function slugify(text) {
  return (text || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80);
}

export default function AdminBlogPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // form object or null
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_super_admin) {
      toast.error("Blog CMS is a Super-Admin tool.");
      navigate("/", { replace: true });
      return;
    }
    refresh();
  }, [user, authLoading]);

  function refresh() {
    setLoading(true);
    api
      .get("/super-admin/blog")
      .then((res) => setPosts(res.data?.posts || []))
      .catch(() => toast.error("Failed to load posts."))
      .finally(() => setLoading(false));
  }

  function openCreate() {
    setEditing({ ...EMPTY_FORM });
    setSlugTouched(false);
  }

  function openEdit(post) {
    setEditing({
      post_id: post.post_id,
      slug: post.slug || "",
      title: post.title || "",
      excerpt: post.excerpt || "",
      body_markdown: post.body_markdown || "",
      featured_image_url: post.featured_image_url || "",
      author_name: post.author_name || "AstroKuberChat Team",
      seo_title: post.seo_title || "",
      seo_description: post.seo_description || "",
      published: !!post.published,
    });
    setSlugTouched(true);
  }

  function patch(field, value) {
    setEditing((e) => ({ ...e, [field]: value }));
  }

  function onTitleChange(v) {
    patch("title", v);
    if (!slugTouched) patch("slug", slugify(v));
  }

  async function save() {
    if (!editing) return;
    if (!editing.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    if (editing.body_markdown.trim().length < 30) {
      toast.error("Body is too short. Write at least a paragraph.");
      return;
    }
    setSaving(true);
    const payload = {
      title: editing.title.trim(),
      slug: editing.slug.trim() || slugify(editing.title),
      excerpt: editing.excerpt.trim(),
      body_markdown: editing.body_markdown,
      featured_image_url: editing.featured_image_url.trim() || null,
      author_name: editing.author_name.trim() || "AstroKuberChat Team",
      seo_title: editing.seo_title.trim() || null,
      seo_description: editing.seo_description.trim() || null,
      published: !!editing.published,
    };
    try {
      if (editing.post_id) {
        await api.put(`/super-admin/blog/${editing.post_id}`, payload);
        toast.success("Post updated.");
      } else {
        await api.post("/super-admin/blog", payload);
        toast.success("Post created.");
      }
      setEditing(null);
      refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Save failed.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  async function remove(post) {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/super-admin/blog/${post.post_id}`);
      toast.success("Post deleted.");
      refresh();
    } catch {
      toast.error("Delete failed.");
    }
  }

  async function togglePublish(post) {
    try {
      await api.put(`/super-admin/blog/${post.post_id}`, {
        title: post.title,
        slug: post.slug,
        excerpt: post.excerpt || "",
        body_markdown: post.body_markdown || "",
        featured_image_url: post.featured_image_url || null,
        author_name: post.author_name || "AstroKuberChat Team",
        seo_title: post.seo_title || null,
        seo_description: post.seo_description || null,
        published: !post.published,
      });
      toast.success(post.published ? "Unpublished." : "Published live.");
      refresh();
    } catch {
      toast.error("Could not toggle publish state.");
    }
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: TH.bg, color: TH.text }} data-testid="admin-blog-page">
      <Header />

      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12 w-full">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm mb-6 hover:opacity-80"
          style={{ color: TH.textMute }}
          data-testid="admin-blog-back"
        >
          <ArrowLeft size={16} /> Back
        </button>

        <header className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em]" style={{ color: TH.accent }}>
              <Article size={14} weight="duotone" /> Super Admin · Blog CMS
            </div>
            <h1 className="font-serif-display text-3xl sm:text-4xl mt-2" style={{ color: TH.text }}>
              Blog posts
            </h1>
            <p className="mt-2 text-sm max-w-2xl" style={{ color: TH.textMute }}>
              Locally-authored posts appear at{" "}
              <Link to="/blog" className="underline" style={{ color: TH.accent }}>
                /blog
              </Link>{" "}
              and override any rankai.ai article with the same slug. Drafts (published = off) are hidden from the public list.
            </p>
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm"
            style={{ background: TH.accent, color: "#1A1410" }}
            data-testid="admin-blog-new-btn"
          >
            <Plus size={16} weight="bold" /> New post
          </button>
        </header>

        {loading ? (
          <div className="grid gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-24 rounded-xl animate-pulse" style={{ background: TH.surface }} />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 rounded-2xl" style={{ background: TH.surface, color: TH.textMute }} data-testid="admin-blog-empty">
            No posts yet. Click "New post" to write one.
          </div>
        ) : (
          <div className="grid gap-3" data-testid="admin-blog-list">
            {posts.map((p) => (
              <div
                key={p.post_id}
                className="rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                style={{ background: TH.surface, border: `1px solid ${TH.border}` }}
                data-testid={`admin-blog-row-${p.slug}`}
              >
                {p.featured_image_url ? (
                  <img src={p.featured_image_url} alt="" className="w-20 h-20 rounded-lg object-cover shrink-0" />
                ) : (
                  <div className="w-20 h-20 rounded-lg shrink-0 flex items-center justify-center" style={{ background: TH.surfaceHi, color: TH.textDim }}>
                    <Article size={28} weight="duotone" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-serif-display text-lg" style={{ color: TH.text }}>
                      {p.title}
                    </h2>
                    {p.published ? (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: "rgba(74,222,156,0.18)", color: TH.green }}>
                        Live
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: "rgba(251,113,133,0.18)", color: TH.red }}>
                        Draft
                      </span>
                    )}
                  </div>
                  <div className="text-xs mt-1 truncate" style={{ color: TH.textMute }}>
                    /blog/{p.slug}
                  </div>
                  {p.excerpt ? (
                    <p className="text-sm mt-1 line-clamp-2" style={{ color: TH.textMute }}>
                      {p.excerpt}
                    </p>
                  ) : null}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {p.published ? (
                    <Link
                      to={`/blog/${p.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="p-2 rounded-lg hover:opacity-80"
                      style={{ background: TH.surfaceHi, color: TH.accent }}
                      title="View live"
                      data-testid={`admin-blog-view-${p.slug}`}
                    >
                      <Globe size={16} />
                    </Link>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => togglePublish(p)}
                    className="p-2 rounded-lg hover:opacity-80"
                    style={{ background: TH.surfaceHi, color: p.published ? TH.red : TH.green }}
                    title={p.published ? "Unpublish" : "Publish"}
                    data-testid={`admin-blog-toggle-${p.slug}`}
                  >
                    {p.published ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => openEdit(p)}
                    className="p-2 rounded-lg hover:opacity-80"
                    style={{ background: TH.surfaceHi, color: TH.text }}
                    title="Edit"
                    data-testid={`admin-blog-edit-${p.slug}`}
                  >
                    <PencilSimple size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(p)}
                    className="p-2 rounded-lg hover:opacity-80"
                    style={{ background: TH.surfaceHi, color: TH.red }}
                    title="Delete"
                    data-testid={`admin-blog-delete-${p.slug}`}
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Editor modal — kept inline because the page is admin-only and
          adding a separate route would clutter the public router. */}
      {editing ? (
        <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-6" style={{ background: "rgba(15,22,35,0.85)" }} data-testid="admin-blog-editor">
          <div className="w-full max-w-3xl rounded-none sm:rounded-2xl overflow-hidden flex flex-col" style={{ background: TH.surface, border: `1px solid ${TH.border}`, maxHeight: "100vh" }}>
            <div className="px-5 py-4 flex items-center justify-between border-b" style={{ borderColor: TH.border }}>
              <h3 className="font-serif-display text-xl" style={{ color: TH.text }}>
                {editing.post_id ? "Edit post" : "New post"}
              </h3>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="text-sm hover:opacity-80"
                style={{ color: TH.textMute }}
                data-testid="admin-blog-editor-cancel"
              >
                Cancel
              </button>
            </div>
            <div className="p-5 overflow-y-auto space-y-4 flex-1">
              <Field label="Title" required>
                <input
                  type="text"
                  value={editing.title}
                  onChange={(e) => onTitleChange(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={inputStyle}
                  placeholder="How to read your Navamsa chart…"
                  data-testid="admin-blog-input-title"
                />
              </Field>
              <Field label="Slug" hint="lowercase, hyphens only. URL will be /blog/your-slug">
                <input
                  type="text"
                  value={editing.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    patch("slug", slugify(e.target.value));
                  }}
                  className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                  style={inputStyle}
                  placeholder="how-to-read-your-navamsa-chart"
                  data-testid="admin-blog-input-slug"
                />
              </Field>
              <Field label="Excerpt" hint="1–2 sentence summary shown on the list + meta description fallback">
                <textarea
                  value={editing.excerpt}
                  onChange={(e) => patch("excerpt", e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ ...inputStyle, minHeight: 60 }}
                  data-testid="admin-blog-input-excerpt"
                />
              </Field>
              <Field label="Featured image URL" hint="16:9 image. Used as hero + Open Graph thumbnail">
                <input
                  type="url"
                  value={editing.featured_image_url}
                  onChange={(e) => patch("featured_image_url", e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={inputStyle}
                  placeholder="https://images.unsplash.com/…"
                  data-testid="admin-blog-input-image"
                />
              </Field>
              <Field label="Body (Markdown)" hint="Supports headings, lists, tables, links, bold/italic, code">
                <textarea
                  value={editing.body_markdown}
                  onChange={(e) => patch("body_markdown", e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                  style={{ ...inputStyle, minHeight: 320 }}
                  data-testid="admin-blog-input-body"
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Author name">
                  <input
                    type="text"
                    value={editing.author_name}
                    onChange={(e) => patch("author_name", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={inputStyle}
                    data-testid="admin-blog-input-author"
                  />
                </Field>
                <Field label="SEO title (optional)">
                  <input
                    type="text"
                    value={editing.seo_title}
                    onChange={(e) => patch("seo_title", e.target.value)}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={inputStyle}
                    placeholder="Defaults to Title"
                    data-testid="admin-blog-input-seotitle"
                  />
                </Field>
              </div>
              <Field label="SEO meta description (optional)">
                <textarea
                  value={editing.seo_description}
                  onChange={(e) => patch("seo_description", e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ ...inputStyle, minHeight: 60 }}
                  placeholder="Defaults to Excerpt"
                  data-testid="admin-blog-input-seodesc"
                />
              </Field>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editing.published}
                  onChange={(e) => patch("published", e.target.checked)}
                  data-testid="admin-blog-input-published"
                />
                <span className="text-sm" style={{ color: TH.text }}>
                  Published (visible at /blog and /blog/:slug)
                </span>
              </label>
            </div>
            <div className="px-5 py-4 border-t flex items-center justify-end gap-3" style={{ borderColor: TH.border }}>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="px-4 py-2 rounded-full text-sm"
                style={{ background: TH.surfaceHi, color: TH.text }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full font-medium text-sm disabled:opacity-50"
                style={{ background: TH.accent, color: "#1A1410" }}
                data-testid="admin-blog-editor-save"
              >
                <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save post"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <Footer />
    </div>
  );
}

const inputStyle = {
  background: "#0F1623",
  color: "#EAF0FA",
  border: "1px solid #2B3A56",
  outline: "none",
};

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <div className="text-xs mb-1.5 flex items-center gap-2" style={{ color: "#90A1BD" }}>
        <span>
          {label}
          {required ? <span style={{ color: "#F5C16C" }}> *</span> : null}
        </span>
        {hint ? <span style={{ color: "#566A8A" }}>· {hint}</span> : null}
      </div>
      {children}
    </label>
  );
}
